<template>
  <BaseDialog :open="open" :labelled-by="titleId" panel-class="max-w-[420px] rounded-2xl p-6" @close="$emit('cancel')">
    <!-- The confirm dialog (the alerts redesign): leads with what will be lost,
         keeps a failure inside the dialog as the same Notice as everywhere. -->
    <div class="flex items-start gap-3.5">
      <span class="w-10 h-10 shrink-0 rounded-full bg-error-bg flex items-center justify-center" aria-hidden="true">
        <Trash2 :size="18" class="text-error" />
      </span>
      <div class="min-w-0 flex flex-col gap-1.5">
        <h3 :id="titleId" class="text-lg leading-[26px] font-semibold text-text">{{ title }}</h3>
        <p class="text-sm leading-[22px] text-muted">{{ message }}</p>
      </div>
    </div>

    <FormField
      v-if="typeToConfirm"
      v-model="typed"
      :label="`Type ${typeToConfirm} to confirm`"
      autocomplete="off"
      autofocus
      :reserve-error-space="false"
      class="mt-4"
    />
    <Notice v-if="error" compact class="mt-4">{{ error }}</Notice>

    <div class="mt-5 flex justify-end gap-2.5">
      <button
        type="button"
        :autofocus="!typeToConfirm"
        :disabled="busy"
        class="h-11 px-[18px] text-sm font-semibold text-text bg-surface border border-border rounded-[10px] hover:bg-canvas disabled:opacity-50 transition-colors"
        @click="$emit('cancel')"
      >
        Cancel
      </button>
      <button
        type="button"
        :disabled="busy || !typedMatches"
        class="h-11 px-[18px] inline-flex items-center gap-2 text-sm font-semibold rounded-[10px] transition-colors disabled:cursor-not-allowed"
        :class="typedMatches ? 'text-white bg-error hover:bg-error-ink disabled:opacity-85' : 'bg-error-border text-error-ink'"
        @click="$emit('confirm')"
      >
        <Loader2 v-if="busy" aria-hidden="true" :size="14" class="animate-spin motion-reduce:animate-none" />
        {{ confirmLabel ?? 'Confirm' }}
      </button>
    </div>
  </BaseDialog>
</template>

<script setup lang="ts">
// Destructive confirmation. Cancel takes focus by default (the least
// destructive key is Enter). `typeToConfirm` adds the "type DELETE" gate.
import { computed, ref, useId, watch } from 'vue'
import { Loader2, Trash2 } from 'lucide-vue-next'
import BaseDialog from '@/components/BaseDialog.vue'
import FormField from '@/components/FormField.vue'
import Notice from '@/components/Notice.vue'

const props = defineProps<{
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  /** Word the user must type before Confirm enables. */
  typeToConfirm?: string
  busy?: boolean
  error?: string
}>()

defineEmits<{
  confirm: []
  cancel: []
}>()

const titleId = useId()
const typed = ref('')
const typedMatches = computed(() => !props.typeToConfirm || typed.value === props.typeToConfirm)

watch(
  () => props.open,
  () => {
    typed.value = ''
  },
)
</script>
